/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminQRCodes\AdminQRCodes.js
 * ВЕРСИЯ: 1.0.0
 * ВРЕМЯ СОЗДАНИЯ: 15.11.2025 11:00
 * 
 * ОПИСАНИЕ: Компонент управления всеми QR кодами в системе
 * - Показывает таблицу всех QR кодов
 * - Фильтрация по статусу (active, claimed, expired)
 * - Поиск по ID или создателю
 * - Кнопки действий (просмотр, отмена, удаление)
 */

import React, { useState, useEffect } from 'react';
import './AdminQRCodes.css';

const AdminQRCodes = () => {
  const [qrCodes, setQRCodes] = useState([]);
  const [filteredQRCodes, setFilteredQRCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchQRCodes();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [qrCodes, searchQuery, statusFilter]);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch('https://qr-money.onrender.com/api/admin/qr-codes', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const qrList = data.data || data.qrCodes || data || [];
      setQRCodes(Array.isArray(qrList) ? qrList : []);
      
      console.log('✅ QR коды загружены:', qrList);
    } catch (err) {
      console.error('❌ Ошибка при загрузке QR кодов:', err);
      setError(err.message);
      setQRCodes(getMockQRCodes());
    } finally {
      setLoading(false);
    }
  };

  const getMockQRCodes = () => [
    {
      id: 1,
      qr_code: 'QR-001-ABC',
      creator_id: 1,
      creator_email: 'alice@test.com',
      amount: 100,
      currency: 'DAR',
      status: 'active',
      claimed_by: null,
      created_at: '2025-11-10T10:00:00Z',
      expires_at: '2025-12-10T10:00:00Z'
    },
    {
      id: 2,
      qr_code: 'QR-002-DEF',
      creator_id: 2,
      creator_email: 'business@test.com',
      amount: 500,
      currency: 'DAR',
      status: 'claimed',
      claimed_by: 1,
      created_at: '2025-11-08T10:00:00Z',
      expires_at: '2025-12-08T10:00:00Z'
    },
    {
      id: 3,
      qr_code: 'QR-003-GHI',
      creator_id: 1,
      creator_email: 'alice@test.com',
      amount: 250,
      currency: 'DAR',
      status: 'expired',
      claimed_by: null,
      created_at: '2025-10-01T10:00:00Z',
      expires_at: '2025-11-01T10:00:00Z'
    }
  ];

  const applyFilters = () => {
    let filtered = qrCodes;

    // Фильтр по статусу
    if (statusFilter !== 'all') {
      filtered = filtered.filter(qr => qr.status === statusFilter);
    }

    // Поиск по QR коду или email
    if (searchQuery.trim()) {
      filtered = filtered.filter(qr =>
        qr.qr_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        qr.creator_email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredQRCodes(filtered);
  };

  const handleRevokeQR = async (qrId) => {
    if (!window.confirm('Вы уверены что хотите отменить этот QR код?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://qr-money.onrender.com/api/admin/qr-codes/${qrId}/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setQRCodes(qrCodes.map(qr =>
          qr.id === qrId ? { ...qr, status: 'revoked' } : qr
        ));
        alert('✅ QR код отменен');
      } else {
        alert('❌ Ошибка при отмене');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при отмене QR кода');
    }
  };

  const handleDeleteQR = async (qrId) => {
    if (!window.confirm('Вы уверены что хотите удалить этот QR код? Это необратимо!')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://qr-money.onrender.com/api/admin/qr-codes/${qrId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setQRCodes(qrCodes.filter(qr => qr.id !== qrId));
        alert('✅ QR код удален');
      } else {
        alert('❌ Ошибка при удалении');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при удалении QR кода');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'claimed':
        return 'status-claimed';
      case 'expired':
        return 'status-expired';
      case 'revoked':
        return 'status-revoked';
      default:
        return 'status-active';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: '✅ Активен',
      claimed: '✔️ Получен',
      expired: '⏰ Истёк',
      revoked: '🚫 Отменён'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="admin-qr-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>⏳ Загрузка QR кодов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-qr-page">
      {/* ЗАГОЛОВОК */}
      <div className="admin-qr-header">
        <h1>🎁 Управление QR кодами</h1>
        <p>Всего QR кодов: {qrCodes.length} | Показано: {filteredQRCodes.length}</p>
      </div>

      {/* ФИЛЬТРЫ */}
      <div className="admin-filters">
        {/* Поиск */}
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Поиск по QR коду или создателю..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Фильтр по статусу */}
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">📊 Все статусы</option>
          <option value="active">✅ Активные</option>
          <option value="claimed">✔️ Получены</option>
          <option value="expired">⏰ Истёкшие</option>
          <option value="revoked">🚫 Отменённые</option>
        </select>

        {/* Кнопка обновления */}
        <button
          className="refresh-btn"
          onClick={fetchQRCodes}
          title="Обновить список"
        >
          🔄 Обновить
        </button>
      </div>

      {/* ОШИБКА */}
      {error && (
        <div className="error-message">
          ⚠️ {error} (используются тестовые данные)
        </div>
      )}

      {/* ТАБЛИЦА */}
      <div className="admin-qr-table">
        {filteredQRCodes.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>QR Код</th>
                <th>Создатель</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th>Получен кем</th>
                <th>Создан</th>
                <th>Истекает</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredQRCodes.map((qr) => (
                <tr key={qr.id}>
                  <td>{qr.id}</td>
                  <td className="qr-code-cell">
                    <code>{qr.qr_code}</code>
                  </td>
                  <td>{qr.creator_email}</td>
                  <td className="amount-cell">
                    <strong>{qr.amount} {qr.currency}</strong>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeColor(qr.status)}`}>
                      {getStatusLabel(qr.status)}
                    </span>
                  </td>
                  <td>{qr.claimed_by ? `ID: ${qr.claimed_by}` : '—'}</td>
                  <td>{new Date(qr.created_at).toLocaleDateString('ru-RU')}</td>
                  <td>{new Date(qr.expires_at).toLocaleDateString('ru-RU')}</td>
                  <td className="actions">
                    <button
                      className="action-btn view"
                      title="Просмотр"
                      onClick={() => alert(`QR: ${qr.qr_code}\nСумма: ${qr.amount} ${qr.currency}\nСоздатель: ${qr.creator_email}`)}
                    >
                      👁️
                    </button>
                    {qr.status === 'active' && (
                      <button
                        className="action-btn revoke"
                        title="Отменить"
                        onClick={() => handleRevokeQR(qr.id)}
                      >
                        ⛔
                      </button>
                    )}
                    <button
                      className="action-btn delete"
                      title="Удалить"
                      onClick={() => handleDeleteQR(qr.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-data">
            <p>❌ QR коды не найдены</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQRCodes;
